import React, { useEffect, useState } from 'react';
import { Bell, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { mccGet, mccPost } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, EmptyState } from './mccUi';
import { humanDateTime } from './mccConstants';

const KIND_TONE = {
  idea_assigned: { bg: '#ede9fe', ink: '#5b21b6' },
  request_assigned: { bg: '#dbeafe', ink: '#1e40af' },
  project_assigned: { bg: '#dbeafe', ink: '#1e40af' },
  project_role_assigned: { bg: '#dbeafe', ink: '#1e40af' },
  filming_assigned: { bg: '#fee2e2', ink: '#991b1b' },
  raw_uploaded: { bg: '#dbeafe', ink: '#1e40af' },
  review_required: { bg: '#ffedd5', ink: '#9a3412' },
  feedback: { bg: '#dbeafe', ink: '#1e40af' },
  approval: { bg: '#dcfce7', ink: '#166534' },
  mentioned: { bg: '#fef3c7', ink: '#78350f' },
};

export default function MccNotifications({ onNav, onOpenProject }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await mccGet('/marketing/notifications');
      setItems(res.items || []); setUnread(res.unread || 0);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const readOne = async (id) => {
    try { await mccPost(`/marketing/notifications/${id}/read`); await load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };
  const readAll = async () => {
    try { await mccPost('/marketing/notifications/read-all', {}); await load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Notifications"
        subtitle="Every assignment, approval, mention and change in one place."
        icon={Bell}
        right={<Button variant="outline" size="sm" onClick={readAll} disabled={!unread}>
          <Check className="w-3 h-3 mr-1" /> Mark all read
        </Button>}
      />

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState icon={Bell} title="Nothing to catch up on" body="Assignments, feedback and approvals will show up here." />
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const tone = KIND_TONE[n.kind] || { bg: '#f1f5f9', ink: '#334155' };
            return (
              <button
                key={n.id}
                onClick={() => {
                  if (n.link?.type === 'project' && n.link?.id) onOpenProject(n.link.id);
                  if (!n.read) readOne(n.id);
                }}
                className={`w-full text-left border rounded-xl p-3 flex items-start gap-3 hover:shadow-sm transition ${n.read ? 'border-slate-200 bg-white' : 'border-indigo-200 bg-indigo-50/40'}`}
                data-testid={`mcc-notif-${n.id}`}
              >
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: tone.bg, color: tone.ink }}
                >{n.kind.replace(/_/g, ' ')}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{humanDateTime(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
